import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';

import {
    Typography, Container, Box, CircularProgress, Alert, Paper, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
    Collapse, Fade, Chip, Avatar, useTheme
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PersonIcon from '@mui/icons-material/Person';

const LinhaAluno = ({ aluno }) => {
    const [aberto, setAberto] = useState(false);
    const getNotaCor = (nota) => {
        const notaNum = parseFloat(nota);
        if (notaNum >= 70) return cores.sucesso.principal;
        if (notaNum >= 50) return cores.aviso.principal;
        return cores.erro.principal;
    };

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    borderBottom: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.04)`
                    }
                }}
            >
                <TableCell
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 1.5,
                        borderBottom: 'none'
                    }}
                >
                    <IconButton
                        aria-label="expandir linha"
                        size="small"
                        onClick={() => setAberto(!aberto)}
                        sx={{
                            mr: 1,
                            color: cores.primaria.principal,
                            backgroundColor: aberto ? `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.08)` : 'transparent',
                            '&:hover': {
                                backgroundColor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.15)`
                            }
                        }}
                    >
                        {aberto ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                mr: 1.5,
                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                color: cores.primaria.principal
                            }}
                        >
                            <PersonIcon fontSize="small" />
                        </Avatar>
                        <Typography sx={{ fontWeight: 500, color: cores.primaria.principal }}>
                            {aluno.nome_completo}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell
                    align="right"
                    sx={{
                        borderBottom: 'none'
                    }}
                >
                    {parseFloat(aluno.media_geral) > 0 ? (
                        <Chip
                            label={aluno.media_geral}
                            sx={{
                                fontWeight: 'bold',
                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                color: getNotaCor(aluno.media_geral),
                                border: `1px solid ${getNotaCor(aluno.media_geral)}`,
                                minWidth: '60px'
                            }}
                        />
                    ) : (
                        <Chip
                            label="Não avaliado"
                            sx={{
                                fontWeight: 'bold',
                                bgcolor: `rgba(${parseInt(cores.cinza.medio.slice(1, 3), 16)}, ${parseInt(cores.cinza.medio.slice(3, 5), 16)}, ${parseInt(cores.cinza.medio.slice(5, 7), 16)}, 0.1)`,
                                color: cores.cinza.medio,
                                border: `1px solid ${cores.cinza.claro}`,
                                minWidth: '110px'
                            }}
                        />
                    )}
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={2}
                    sx={{
                        borderBottom: aberto ? 'none' : `1px solid ${cores.cinza.claro}`
                    }}
                >
                    <Collapse in={aberto} timeout="auto" unmountOnExit>
                        <Box sx={{
                            margin: 2,
                            backgroundColor: cores.fundo.claro,
                            borderRadius: 2,
                            p: 2,
                            border: `1px solid ${cores.cinza.claro}`
                        }}>
                            <Typography
                                variant="subtitle1"
                                gutterBottom
                                component="div"
                                sx={{
                                    fontWeight: 600,
                                    color: cores.primaria.principal,
                                    mb: 2,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <AssessmentIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                                Desempenho por Caso Clínico
                            </Typography>
                            {aluno.desempenho_por_caso.length > 0 ? (
                                <Table size="small" aria-label="detalhes" sx={{ border: `1px solid ${cores.cinza.claro}`, borderRadius: 1 }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.05)` }}>
                                            <TableCell sx={{ fontWeight: 600, color: cores.primaria.principal }}>Caso Clínico</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: cores.primaria.principal }}>Nota Final</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {aluno.desempenho_por_caso.map((caso, index) => (
                                            <TableRow key={index} sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                '&:hover': { backgroundColor: cores.fundo.claro }
                                            }}>
                                                <TableCell component="th" scope="row">
                                                    <Typography
                                                        sx={{
                                                            color: cores.primaria.principal,
                                                            fontWeight: 500,
                                                            fontSize: '0.875rem',
                                                            pl: 1
                                                        }}
                                                    >
                                                        {caso.caso_titulo}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={caso.nota_final}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            bgcolor: 'transparent',
                                                            color: getNotaCor(caso.nota_final),
                                                            border: `1px solid ${getNotaCor(caso.nota_final)}`
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Box sx={{
                                    py: 3,
                                    textAlign: 'center',
                                    border: `1px dashed ${cores.cinza.claro}`,
                                    borderRadius: 1,
                                    backgroundColor: cores.fundo.claro
                                }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: cores.cinza.medio,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        O aluno ainda não possui avaliações em nenhum caso clínico.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            {/* Linha de separação que só aparece quando o detalhe está expandido */}
            {aberto && (
                <TableRow sx={{ height: '1px' }}>
                    <TableCell
                        colSpan={2}
                        sx={{
                            p: 0,
                            borderBottom: `1px solid ${cores.cinza.claro}`
                        }}
                    />
                </TableRow>
            )}
        </React.Fragment>
    );
};


const RelatorioGrupoPagina = () => {
    const { grupoId } = useParams();
    const navegar = useNavigate();
    const theme = useTheme();

    const [grupo, setGrupo] = useState(null);
    const [relatorio, setRelatorio] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');

    const buscarDados = useCallback(async () => {
        try {
            const [respRelatorio, respGrupo] = await Promise.all([
                api.get(`/api/relatorios/grupo/${grupoId}`),
                api.get(`/api/grupos/${grupoId}/detalhes`)
            ]);
            setRelatorio(respRelatorio.data);
            setGrupo(respGrupo.data.grupo);
        } catch (err) {
            setErro('Falha ao carregar o relatório do grupo.');
        } finally {
            setCarregando(false);
        }
    }, [grupoId]);

    useEffect(() => {
        buscarDados();
    }, [buscarDados]);

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    if (erro) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        backgroundColor: cores.erro.claro,
                        borderColor: cores.erro.medio,
                        color: cores.erro.escuro,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    {erro}
                </Alert>
            </Container>
        );
    }

    if (!relatorio || !grupo) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="info"
                    sx={{
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    Dados não encontrados.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar(`/minha-turma/${grupo.turma_id}`)}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Relatório de Desempenho
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 0.5
                                }}
                            >
                                <AssessmentIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                Grupo: {grupo.nome}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3, px: { xs: 1, sm: 0 } }}>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                color: cores.primaria.principal,
                                mb: 1,
                                fontSize: { xs: '1.25rem', sm: '1.5rem' }
                            }}
                        >
                            Desempenho dos Alunos
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: cores.texto.secundario,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                        >
                            Visualize o desempenho de cada aluno nos casos clínicos
                        </Typography>
                    </Box>

                    <Paper elevation={0} sx={{
                        mt: 2,
                        overflow: 'hidden',
                        border: `1px solid ${cores.cinza.claro}`,
                        borderRadius: 2
                    }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.05)`,
                                        '& th': {
                                            borderBottom: `2px solid ${cores.cinza.claro}`
                                        }
                                    }}>
                                        <TableCell sx={{
                                            fontWeight: 'bold',
                                            color: cores.primaria.principal,
                                            fontSize: '0.95rem',
                                            py: 2
                                        }}>Nome do Aluno</TableCell>
                                        <TableCell align="right" sx={{
                                            fontWeight: 'bold',
                                            color: cores.primaria.principal,
                                            fontSize: '0.95rem',
                                            py: 2
                                        }}>Média Geral</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {relatorio.length > 0 ? (
                                        relatorio.map((aluno) => (
                                            <LinhaAluno key={aluno.aluno_id} aluno={aluno} />
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4, color: cores.texto.secundario }}>
                                                Nenhum dado de desempenho disponível para este grupo.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Paper>
            </Fade>
        </Container>
    );
};

export default RelatorioGrupoPagina;